import { stringify } from "query-string";

import { AxiosInstance } from "@bps/http-client";
import { toQueryResult, withPaging } from "@libs/api/utils/paging.utils.ts";
import { appContext } from "@libs/config/app-context.utils.ts";
import {
  GetXeroFinancialSyncDataFilters,
  XeroAccountMappingsAddDto,
  XeroAccountMappingsDto,
  XeroFetchedAccountDto,
  XeroFinancialSyncAllRequestDto,
  XeroFinancialSyncData,
  XeroFinancialSyncGetRequestArgsDto,
  XeroFinancialSyncGetRequestDto,
  XeroFinancialSyncSource,
  XeroSettingsAddDto,
  XeroSettingsDto,
  XeroSettingsUpdateDto,
  XeroTenantInfoDto
} from "@libs/gateways/custom-integration/CustomIntegrationXeroGateway.dtos.ts";
import { ICustomIntegrationXeroGateway } from "@libs/gateways/custom-integration/ICustomIntegrationXeroGateway.interface.ts";
import { QueryResult } from "@libs/utils/promise-observable/promise-observable.utils.ts";

export class CustomIntegrationXeroGateway
  implements ICustomIntegrationXeroGateway
{
  constructor(private api: AxiosInstance) {}

  public async addXeroTenant(code: string): Promise<XeroTenantInfoDto> {
    const request = {
      code,
      redirectUri: encodeURIComponent(appContext.config.xeroRedirectUrl)
    };

    const response = await this.api.post<XeroTenantInfoDto>(
      "/xero/requestToken",
      request
    );
    return response.data;
  }

  public async getXeroTenantInfo(): Promise<XeroTenantInfoDto> {
    const response = await this.api.get<XeroTenantInfoDto>("/xeroTenantInfo");
    return response.data;
  }

  public async setXeroTenant(tenantId: string): Promise<void> {
    const requestData = {
      tenantId
    };

    const response = await this.api.post(
      `/xeroTenantInfo?${stringify(requestData)}`
    );
    return response.data;
  }

  public async postFinancialSyncAll(): Promise<XeroFinancialSyncAllRequestDto> {
    const response =
      await this.api.post<XeroFinancialSyncAllRequestDto>("/financialSyncAll");
    return response.data;
  }

  public async getFinancialSyncAllLatest(): Promise<XeroFinancialSyncAllRequestDto> {
    const response = await this.api.get<XeroFinancialSyncAllRequestDto>(
      "/financialSyncAll/latest"
    );
    return response.data;
  }

  public async getFinancialSyncData(
    filter: GetXeroFinancialSyncDataFilters
  ): Promise<QueryResult<XeroFinancialSyncData>> {
    const queryString = withPaging(filter);

    const response = await this.api.get<XeroFinancialSyncData[]>(
      `/financialSyncData?${stringify(queryString)}`
    );

    return toQueryResult(response);
  }

  public async getFinancialSyncSource(
    filter: GetXeroFinancialSyncDataFilters
  ): Promise<QueryResult<XeroFinancialSyncSource>> {
    const queryString = withPaging(filter);

    const response = await this.api.get<XeroFinancialSyncSource[]>(
      `/financialSyncSources?${stringify(queryString)}`
    );

    return toQueryResult(response);
  }

  public async postFinancialSync(
    syncGetType: string
  ): Promise<XeroFinancialSyncGetRequestDto> {
    const response = await this.api.post<XeroFinancialSyncGetRequestDto>(
      "/financialSyncGet",
      syncGetType
    );
    return response.data;
  }

  public async getFinancialSync(
    requestId: string
  ): Promise<XeroFinancialSyncGetRequestDto> {
    const response = await this.api.get<XeroFinancialSyncGetRequestDto>(
      `/financialSyncGet/${requestId}`
    );
    return response.data;
  }

  public async getFinancialByArgsSync(
    targetTenantId: XeroFinancialSyncGetRequestArgsDto
  ): Promise<XeroFinancialSyncGetRequestDto> {
    const response = await this.api.get<XeroFinancialSyncGetRequestDto>(
      `/financialSyncGet/${targetTenantId}/GetByArgs`
    );
    return response.data;
  }

  public async getFetchedAccount(): Promise<XeroFetchedAccountDto[]> {
    const response =
      await this.api.get<XeroFetchedAccountDto[]>("/fetchedAccounts");
    return response.data;
  }

  public async getAccountMappings(): Promise<XeroAccountMappingsDto[]> {
    const response =
      await this.api.get<XeroAccountMappingsDto[]>("/accountMappings");
    return response.data;
  }

  public async postAccountMappings(request: XeroAccountMappingsAddDto) {
    const response = await this.api.post<XeroAccountMappingsDto>(
      "/accountMappings",
      request
    );
    return response.data;
  }

  public async updateAccountMappings(
    request: XeroAccountMappingsDto
  ): Promise<XeroAccountMappingsDto> {
    const response = await this.api.put<XeroAccountMappingsDto>(
      "/accountMappings",
      request
    );

    return response.data;
  }

  public async deleteConnection(tenantId: string): Promise<XeroTenantInfoDto> {
    const response = await this.api.post<XeroTenantInfoDto>(
      `/xeroTenantInfo/${tenantId}/deleteConnection`
    );
    return response.data;
  }

  public async getSettings(): Promise<XeroSettingsDto> {
    const response = await this.api.get<XeroSettingsDto>(
      "/financialSyncSettings"
    );
    return response.data;
  }

  public async addSettings(
    request: XeroSettingsAddDto
  ): Promise<XeroSettingsDto> {
    const response = await this.api.post<XeroSettingsDto>(
      "/financialSyncSettings",
      request
    );
    return response.data;
  }

  public async updateSettings(
    request: XeroSettingsUpdateDto
  ): Promise<XeroSettingsDto> {
    const response = await this.api.put<XeroSettingsDto>(
      "/financialSyncSettings",
      request
    );
    return response.data;
  }
}
